/**
 * Контракт: ліміт незареєстрованих контактів в одному персональному запрошенні.
 *
 * Чисті функції без Vue та i18n. Ліміт береться з CASL-правила `can('invite', 'UnknownEmailsLimit', { limit })`
 * (будується в store/actions/authActions.js лише з цілим limit >= 0; немає правила = немає клієнтського ліміту;
 * 0 = активний ліміт, усі незареєстровані червоні). Читати правило — через `unknownEmailsLimitFromAbility`.
 */

const UNKNOWN_EMAILS_LIMIT_REASON = 'UnknownEmailsLimitExceeded'

function safeParse(text) {
  try {
    const result = JSON.parse(text)
    return result && typeof result === 'object' ? result : null
  } catch (_) {
    return null
  }
}

/**
 * @param {string} email
 * @returns {string} trim + lowercase
 */
export function normalizeEmail(email) {
  return `${email ?? ''}`.trim().toLowerCase()
}

/**
 * Множина K: нормалізовані email уже запрошених незареєстрованих (organizationId null або 0).
 * @param {Array} members групи (`users`) або плоскі елементи
 * @returns {Set<string>}
 */
export function collectInvitedUnregistered(members) {
  const result = new Set()
  const addUser = (user, group) => {
    const organizationId = user?.organizationId ?? group?.organizationId
    if (!user?.email || (organizationId !== null && organizationId !== undefined && organizationId !== 0)) {
      return
    }
    result.add(normalizeEmail(user.email))
  }
  for (const item of members || []) {
    if (Array.isArray(item?.users)) {
      item.users.forEach(user => addUser(user, item))
    } else {
      addUser(item)
    }
  }
  return result
}

/**
 * Проходить пошти діалогу по порядку. invalid / blockDomen та зареєстровані не займають слот;
 * not_registered, що вже в invitedSet (K), не займає слот; решта not_registered займає слот,
 * доки K.size + взяті < limit, інші повертаються як перевищення ліміту.
 * @param {Array<{ email: string, not_registered?: boolean, invalid?: boolean, blockDomen?: boolean }>} mails
 * @param {number} limit
 * @param {Set<string>} invitedSet
 * @returns {Set<string>} нормалізовані email понад ліміт
 */
export function overLimitEmails(mails, limit, invitedSet) {
  const invited = invitedSet || new Set()
  const taken = new Set()
  const result = new Set()
  for (const mail of mails || []) {
    if (mail.invalid || mail.blockDomen || !mail.not_registered) {
      continue
    }
    const email = normalizeEmail(mail.email)
    if (invited.has(email) || taken.has(email)) {
      continue
    }
    if (invited.size + taken.size < limit) {
      taken.add(email)
    } else {
      result.add(email)
    }
  }
  return result
}

/**
 * Чи є серед помилок відмова бекенду за лімітом: шлюз кладе сире тіло 400 у `errors[].message`
 * (JSON, без парсингу не читається).
 * @param {Array<{ message?: string }>} errors
 * @returns {boolean}
 */
export function isUnknownEmailsLimitRejection(errors) {
  return (errors || []).some(error => {
    const body = safeParse(error?.message)
    if (!body) {
      return false
    }
    const entries = Array.isArray(body) ? body : (Array.isArray(body.errors) ? body.errors : [body])
    return entries.some(entry => entry?.reason === UNKNOWN_EMAILS_LIMIT_REASON)
  })
}

/**
 * Читає ліміт з умови CASL-правила `can('invite', 'UnknownEmailsLimit', { limit })` через публічний API
 * (`relevantRuleFor(...).conditions.limit`). Чи є правило, перевіряє той, хто викликає (`$can`).
 * @param {{ relevantRuleFor: Function }|null|undefined} ability
 * @returns {number|null} null, якщо правила немає
 */
export function unknownEmailsLimitFromAbility(ability) {
  return ability?.relevantRuleFor('invite', 'UnknownEmailsLimit')?.conditions?.limit ?? null
}
